package com.partejar.app.feature.contratos.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LocalLifecycleOwner
import androidx.lifecycle.repeatOnLifecycle
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.format.formatBrazilianDateTime
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.contratos.data.*
import org.koin.compose.koinInject
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlin.time.Clock

private enum class ContractPage(val title: String) {
    PREPARATION("Preparar contrato"),
    DETAIL("Contrato"),
    PLAN("Escolher condição comercial"),
    MODEL("Escolher modelo"),
    SIGNERS("Signatários"),
    CIVIL_DATA("Dados da paciente")
}

@Composable
fun ContratoScreen(
    patientId: Long,
    onBack: () -> Unit,
    onOpenPatientAfterCompletion: () -> Unit,
    onEditPatient: () -> Unit,
    reloadKey: Int = 0,
    viewModel: ContratoViewModel = koinInject(),
    onCreatePlan: () -> Unit = {},
    onCreateContractTemplate: () -> Unit = {}
) {
    val state by viewModel.state.collectAsState()
    val pdfHandler = rememberContractPdfHandler()
    var page by remember(patientId) { mutableStateOf(ContractPage.DETAIL) }
    var cancelConfirmation by remember(patientId) { mutableStateOf(false) }
    var teamSignatureDialog by remember(patientId) { mutableStateOf(false) }
    var planId by remember(patientId) { mutableStateOf<Long?>(null) }
    var modelId by remember(patientId) { mutableStateOf<Long?>(null) }
    var signerIds by remember(patientId) { mutableStateOf(emptySet<Long>()) }
    val latestPage by rememberUpdatedState(page)

    LaunchedEffect(patientId, reloadKey) { viewModel.loadOrStart(patientId) }
    val lifecycleOwner = LocalLifecycleOwner.current
    LaunchedEffect(viewModel, patientId, lifecycleOwner) {
        lifecycleOwner.lifecycle.repeatOnLifecycle(Lifecycle.State.RESUMED) {
            viewModel.observePendingSignatures()
        }
    }
    LaunchedEffect(viewModel) {
        viewModel.completedAction.collect {
            if (latestPage != ContractPage.PREPARATION) page = ContractPage.DETAIL
        }
    }

    val content = state as? ContratoUiState.Content
    LaunchedEffect(
        content?.contract?.id,
        content?.contract?.planVersionId,
        content?.contract?.modelVersionId,
        content?.contract?.signerIds
    ) {
        content?.contract?.let { contract ->
            planId = contract.planVersionId
            modelId = contract.modelVersionId
            signerIds = contract.signerIds.toSet()
        }
    }
    LaunchedEffect(content?.contract?.id, content?.preparation?.active) {
        when {
            content?.preparation?.active == true -> page = ContractPage.PREPARATION
            page == ContractPage.PREPARATION -> page = ContractPage.DETAIL
        }
    }
    LaunchedEffect(teamSignatureDialog, content?.signatures?.canCurrentMemberSign, content?.submitting) {
        if (teamSignatureDialog && content?.submitting == false && content.signatures?.canCurrentMemberSign == false) {
            teamSignatureDialog = false
        }
    }

    Scaffold(
        topBar = {
            PartejarTopBar(
                if (state is ContratoUiState.SetupRequired) "Preparar contrato" else page.title,
                leadingContent = {
                    PartejarIconButton(onClick = {
                        if (page == ContractPage.DETAIL || page == ContractPage.PREPARATION) {
                            if (shouldOpenPatientAfterContract(content?.contract?.status)) {
                                onOpenPatientAfterCompletion()
                            } else {
                                onBack()
                            }
                        } else {
                            page = ContractPage.DETAIL
                        }
                    }) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        when (val current = state) {
            ContratoUiState.Loading -> ContractLoading(Modifier.padding(padding))
            is ContratoUiState.SetupRequired -> Column(
                Modifier.fillMaxSize().padding(padding).verticalScroll(rememberScrollState()).padding(PartejarTheme.spacing.space16),
                verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)
            ) {
                ContractSetupRequired(current.requirements, onCreatePlan, onCreateContractTemplate)
                PartejarButton("Atualizar progresso", { viewModel.loadOrStart(patientId) }, variant = PartejarButtonVariant.TERTIARY,
                    shape = PartejarShapes.pill, leadingIcon = { Icon(PartejarIcons.Relationships.followUp, null, Modifier.size(PartejarTheme.dimensions.iconMedium)) })
            }
            is ContratoUiState.Error -> ContractLoadError(
                message = current.message,
                onRetry = { viewModel.loadOrStart(patientId) },
                modifier = Modifier.padding(padding)
            )
            is ContratoUiState.Content -> {
                current.pdf?.let { pdf -> LaunchedEffect(pdf) {
                    viewModel.pdfHandled(openContractPdf(pdfHandler, pdf))
                } }

                val saveConfiguration: (Long?, Long?, Set<Long>) -> Unit = { selectedPlan, selectedModel, selectedSigners ->
                    planId = selectedPlan
                    modelId = selectedModel
                    signerIds = selectedSigners
                    if (selectedPlan != null && selectedModel != null && selectedSigners.isNotEmpty()) {
                        viewModel.configure(selectedPlan, selectedModel, selectedSigners.toList())
                    } else {
                        page = ContractPage.DETAIL
                    }
                }

                when (page) {
                    ContractPage.PREPARATION -> ContractPreparationFlow(
                        state = current,
                        selectedPlanId = planId,
                        selectedModelId = modelId,
                        selectedSignerIds = signerIds,
                        onPlanChange = { planId = it },
                        onModelChange = { modelId = it },
                        onSignerChange = { signerIds = it },
                        onSavePregnancy = viewModel::updatePregnancy,
                        onSaveCivil = viewModel::updateCivil,
                        onSaveConfiguration = { selectedPlan, selectedModel, selectedSigners ->
                            viewModel.configure(selectedPlan, selectedModel, selectedSigners.toList())
                        },
                        onGenerate = viewModel::generate,
                        onCancel = onBack,
                        modifier = Modifier.padding(padding)
                    )
                    ContractPage.DETAIL -> ContractDetail(
                        state = current,
                        selectedPlanId = planId,
                        selectedModelId = modelId,
                        selectedSignerIds = signerIds,
                        onEditPatient = onEditPatient,
                        onSelectPlan = { page = ContractPage.PLAN },
                        onSelectModel = { page = ContractPage.MODEL },
                        onSelectSigners = { page = ContractPage.SIGNERS },
                        onEditCivilData = { page = ContractPage.CIVIL_DATA },
                        onGenerate = viewModel::generate,
                        onPdf = viewModel::openPdf,
                        onLink = viewModel::generateSignatureLink,
                        onSignTeam = { teamSignatureDialog = true },
                        onCancel = { cancelConfirmation = true },
                        modifier = Modifier.padding(padding)
                    )
                    ContractPage.PLAN -> PlanSelection(
                        options = current.options.plans,
                        selectedId = planId,
                        submitting = current.submitting,
                        error = current.actionError,
                        onConfirm = { saveConfiguration(it, modelId, signerIds) },
                        modifier = Modifier.padding(padding)
                    )
                    ContractPage.MODEL -> ModelSelection(
                        options = current.options.models,
                        selectedId = modelId,
                        submitting = current.submitting,
                        error = current.actionError,
                        onConfirm = { saveConfiguration(planId, it, signerIds) },
                        modifier = Modifier.padding(padding)
                    )
                    ContractPage.SIGNERS -> SignerSelection(
                        options = current.options.signers,
                        selectedIds = signerIds,
                        submitting = current.submitting,
                        error = current.actionError,
                        onConfirm = { saveConfiguration(planId, modelId, it) },
                        modifier = Modifier.padding(padding)
                    )
                    ContractPage.CIVIL_DATA -> CivilDataEditor(
                        civil = current.civil,
                        submitting = current.submitting,
                        error = current.actionError,
                        onSave = viewModel::updateCivil,
                        onCancel = { page = ContractPage.DETAIL },
                        modifier = Modifier.padding(padding)
                    )
                }
            }
        }
    }

    if (cancelConfirmation) {
        AlertDialog(
            onDismissRequest = { cancelConfirmation = false },
            icon = { Icon(PartejarIcons.Feedback.attention, contentDescription = null) },
            title = { Text("Cancelar contrato?") },
            text = { Text("O contrato ficará disponível somente para consulta e não poderá ser editado novamente.") },
            confirmButton = {
                TextButton(onClick = {
                    cancelConfirmation = false
                    viewModel.cancel()
                }) { Text("Cancelar contrato", color = MaterialTheme.colorScheme.error) }
            },
            dismissButton = {
                TextButton(onClick = { cancelConfirmation = false }) { Text("Manter contrato") }
            }
        )
    }

    if (teamSignatureDialog && content != null) {
        TeamSignatureDialog(
            submitting = content.submitting,
            error = content.actionError,
            onDismiss = { teamSignatureDialog = false },
            onSign = viewModel::signAsTeam
        )
    }
}

@Composable
private fun TeamSignatureDialog(
    submitting: Boolean,
    error: String?,
    onDismiss: () -> Unit,
    onSign: (String) -> Unit
) {
    var accepted by remember { mutableStateOf(false) }
    var strokes by remember { mutableStateOf(emptyList<List<SignaturePoint>>()) }
    var attempted by remember { mutableStateOf(false) }
    val canSubmit = canSubmitSignature(accepted, strokes, submitting)
    val spacing = PartejarTheme.spacing

    AlertDialog(
        onDismissRequest = { if (!submitting) onDismiss() },
        icon = { Icon(PartejarIcons.Contracts.signature, contentDescription = null) },
        title = { Text("Assinar contrato") },
        text = {
            Column(
                Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(spacing.space12)
            ) {
                Text("Desenhe sua assinatura abaixo. Ela será anexada às evidências do contrato.")
                ContractSignaturePad(strokes, onChange = { strokes = it })
                PartejarButton(
                    "Limpar assinatura",
                    { strokes = emptyList() },
                    variant = PartejarButtonVariant.SECONDARY,
                    enabled = strokes.isNotEmpty() && !submitting
                )
                Row(
                    Modifier
                        .fillMaxWidth()
                        .toggleable(
                            value = accepted,
                            enabled = !submitting,
                            role = Role.Checkbox,
                            onValueChange = { accepted = it }
                        )
                        .padding(vertical = spacing.space8),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Checkbox(accepted, onCheckedChange = null, enabled = !submitting)
                    Spacer(Modifier.width(spacing.space8))
                    Text("Li o contrato e confirmo esta assinatura.")
                }
                error?.takeIf { attempted }?.let {
                    PartejarAlert("Não foi possível assinar", PartejarAlertTone.ERROR) { Text(it) }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    if (canSubmit) {
                        attempted = true
                        onSign(serializeSignatureStrokes(strokes))
                    }
                },
                enabled = canSubmit
            ) { Text(if (submitting) "Assinando..." else "Confirmar assinatura") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !submitting) { Text("Cancelar") }
        }
    )
}

@Composable
private fun ContractPreparationFlow(
    state: ContratoUiState.Content,
    selectedPlanId: Long?,
    selectedModelId: Long?,
    selectedSignerIds: Set<Long>,
    onPlanChange: (Long) -> Unit,
    onModelChange: (Long) -> Unit,
    onSignerChange: (Set<Long>) -> Unit,
    onSavePregnancy: (String, String, String) -> Unit,
    onSaveCivil: (String, String) -> Unit,
    onSaveConfiguration: (Long, Long, Set<Long>) -> Unit,
    onGenerate: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    val progress = state.preparation
    Column(modifier.fillMaxSize()) {
        ContractPreparationStepper(progress)
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        when (progress.current) {
            ContractPreparationStep.PREGNANCY -> PregnancyPreparationStep(
                state = state,
                onContinue = onSavePregnancy,
                onCancel = onCancel,
                modifier = Modifier.weight(1f)
            )
            ContractPreparationStep.PATIENT -> PatientPreparationStep(
                state = state,
                onContinue = onSaveCivil,
                onCancel = onCancel,
                modifier = Modifier.weight(1f)
            )
            ContractPreparationStep.CONTRACT -> ContractConfigurationPreparationStep(
                state = state,
                selectedPlanId = selectedPlanId,
                selectedModelId = selectedModelId,
                selectedSignerIds = selectedSignerIds,
                onPlanChange = onPlanChange,
                onModelChange = onModelChange,
                onSignerChange = onSignerChange,
                onContinue = onSaveConfiguration,
                onCancel = onCancel,
                modifier = Modifier.weight(1f)
            )
            ContractPreparationStep.REVIEW -> ContractReviewPreparationStep(
                state = state,
                selectedPlanId = selectedPlanId,
                selectedModelId = selectedModelId,
                selectedSignerIds = selectedSignerIds,
                onGenerate = onGenerate,
                onCancel = onCancel,
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ContractPreparationStepper(progress: ContractPreparationProgress) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Column(
        Modifier.fillMaxWidth().padding(horizontal = spacing.space16, vertical = spacing.space12),
        verticalArrangement = Arrangement.spacedBy(spacing.space8)
    ) {
        Text(
            "Etapa ${progress.currentIndex + 1} de ${progress.steps.size} · ${progress.current.title}",
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.primary
        )
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
            progress.steps.forEachIndexed { index, step ->
                val completed = step in progress.completed
                val current = index == progress.currentIndex
                Column(
                    Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(spacing.space4)
                ) {
                    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier.weight(1f).height(dimensions.strokeThin),
                            contentAlignment = Alignment.Center
                        ) {
                            if (index > 0) HorizontalDivider(
                                color = if (completed || current) MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.outlineVariant
                            )
                        }
                        Surface(
                            modifier = Modifier.size(spacing.space24),
                            shape = CircleShape,
                            color = when {
                                completed -> MaterialTheme.colorScheme.primary
                                current -> MaterialTheme.colorScheme.primaryContainer
                                else -> MaterialTheme.colorScheme.surfaceContainerHighest
                            },
                            contentColor = when {
                                completed -> MaterialTheme.colorScheme.onPrimary
                                current -> MaterialTheme.colorScheme.onPrimaryContainer
                                else -> MaterialTheme.colorScheme.onSurfaceVariant
                            }
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(if (completed) "✓" else "${index + 1}", style = MaterialTheme.typography.labelSmall)
                            }
                        }
                        Box(Modifier.weight(1f).height(dimensions.strokeThin)) {
                            if (index < progress.steps.lastIndex) HorizontalDivider(
                                color = if (completed) MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.outlineVariant
                            )
                        }
                    }
                    Text(
                        step.title,
                        style = MaterialTheme.typography.labelSmall,
                        color = if (current || completed) MaterialTheme.colorScheme.onSurface
                        else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }
    }
}

@Composable
private fun PregnancyPreparationStep(
    state: ContratoUiState.Content,
    onContinue: (String, String, String) -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    val patient = state.patient
    var babyName by remember(patient.id, patient.babyName) { mutableStateOf(patient.babyName.orEmpty()) }
    var dueDate by remember(patient.id, patient.dueDate) {
        mutableStateOf(patient.dueDate?.let(::formatBrazilianDate).orEmpty())
    }
    var maternity by remember(patient.id, patient.maternity) { mutableStateOf(patient.maternity.orEmpty()) }
    val dueDateIso = parseBrazilianDate(dueDate)?.toString()
    PreparationStepLayout(
        title = "Dados da gestação",
        description = "Antes de gerar o contrato, confirme o nome do bebê e a DPP. A maternidade pode ficar em branco.",
        error = state.actionError,
        primaryLabel = "Salvar e continuar",
        primaryEnabled = babyName.isNotBlank() && dueDateIso != null && !state.submitting,
        submitting = state.submitting,
        onPrimary = { onContinue(babyName, requireNotNull(dueDateIso), maternity) },
        onCancel = onCancel,
        modifier = modifier
    ) {
        val spacing = PartejarTheme.spacing
        val focus = LocalFocusManager.current
        val dueDateFocus = remember { FocusRequester() }
        val maternityFocus = remember { FocusRequester() }
        PartejarTextField(
            value = babyName,
            onValueChange = { babyName = it },
            label = "Nome do bebê",
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
            keyboardActions = KeyboardActions(onNext = { dueDateFocus.requestFocus() })
        )
        PartejarDateField(
            value = dueDate,
            onValueChange = { dueDate = it },
            label = "DPP",
            error = if (dueDate.isNotBlank() && dueDateIso == null) "Informe uma data válida." else null,
            modifier = Modifier.fillMaxWidth().focusRequester(dueDateFocus)
        )
        PartejarTextField(
            value = maternity,
            onValueChange = { maternity = it },
            label = "Maternidade (opcional)",
            modifier = Modifier.fillMaxWidth().focusRequester(maternityFocus),
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(onDone = { focus.clearFocus() })
        )
        Text(
            "Você poderá informar ou alterar a maternidade depois.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(top = spacing.space4)
        )
    }
}

@Composable
private fun PatientPreparationStep(
    state: ContratoUiState.Content,
    onContinue: (String, String) -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    var cpf by remember(state.contract.id, state.civil.cpf) { mutableStateOf(state.civil.cpf.orEmpty()) }
    var email by remember(state.contract.id, state.civil.email) { mutableStateOf(state.civil.email.orEmpty()) }
    PreparationStepLayout(
        title = "Dados da gestante",
        description = "Complete somente os dados civis que ainda faltam para o documento.",
        error = state.actionError,
        primaryLabel = "Salvar e continuar",
        primaryEnabled = cpf.isNotBlank() && email.isNotBlank() && !state.submitting,
        submitting = state.submitting,
        onPrimary = { onContinue(cpf, email) },
        onCancel = onCancel,
        modifier = modifier
    ) {
        val focus = LocalFocusManager.current
        val emailFocus = remember { FocusRequester() }
        PartejarTextField(
            value = cpf,
            onValueChange = { cpf = it },
            label = "CPF",
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number, imeAction = ImeAction.Next),
            keyboardActions = KeyboardActions(onNext = { emailFocus.requestFocus() })
        )
        PartejarTextField(
            value = email,
            onValueChange = { email = it },
            label = "E-mail",
            modifier = Modifier.fillMaxWidth().focusRequester(emailFocus),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email, imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(onDone = { focus.clearFocus() })
        )
    }
}

@Composable
private fun ContractConfigurationPreparationStep(
    state: ContratoUiState.Content,
    selectedPlanId: Long?,
    selectedModelId: Long?,
    selectedSignerIds: Set<Long>,
    onPlanChange: (Long) -> Unit,
    onModelChange: (Long) -> Unit,
    onSignerChange: (Set<Long>) -> Unit,
    onContinue: (Long, Long, Set<Long>) -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    PreparationStepLayout(
        title = "Configuração do contrato",
        description = "Escolha a condição comercial, o modelo e quem assinará pela equipe.",
        error = state.actionError,
        primaryLabel = "Salvar e revisar",
        primaryEnabled = selectedPlanId != null && selectedModelId != null && selectedSignerIds.isNotEmpty() && !state.submitting,
        submitting = state.submitting,
        onPrimary = {
            onContinue(requireNotNull(selectedPlanId), requireNotNull(selectedModelId), selectedSignerIds)
        },
        onCancel = onCancel,
        modifier = modifier
    ) {
        SectionTitle("Condição comercial")
        state.options.plans.forEach { plan ->
            SelectionOption(selected = selectedPlanId == plan.versionId, onClick = { onPlanChange(plan.versionId) }) {
                Text(plan.name, style = MaterialTheme.typography.titleSmall)
                Text(formatContractPrice(plan.price), color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
        SectionTitle("Modelo")
        state.options.models.forEach { model ->
            SelectionOption(selected = selectedModelId == model.versionId, onClick = { onModelChange(model.versionId) }) {
                Text(model.name, style = MaterialTheme.typography.titleSmall)
            }
        }
        SectionTitle("Signatários da equipe")
        state.options.signers.forEach { signer ->
            SignerOption(
                signer = signer,
                selected = signer.memberId in selectedSignerIds,
                onChange = { selected ->
                    onSignerChange(
                        if (selected) selectedSignerIds + signer.memberId else selectedSignerIds - signer.memberId
                    )
                }
            )
        }
    }
}

@Composable
private fun ContractReviewPreparationStep(
    state: ContratoUiState.Content,
    selectedPlanId: Long?,
    selectedModelId: Long?,
    selectedSignerIds: Set<Long>,
    onGenerate: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    val plan = state.options.plans.firstOrNull { it.versionId == selectedPlanId }
        ?: state.options.plans.firstOrNull { it.versionId == state.contract.planVersionId }
    val model = state.options.models.firstOrNull { it.versionId == selectedModelId }
        ?: state.options.models.firstOrNull { it.versionId == state.contract.modelVersionId }
    val signers = state.options.signers.filter {
        it.memberId in selectedSignerIds || it.memberId in state.contract.signerIds
    }
    val ready = contractPrerequisitesReady(state.patient.babyName, state.patient.dueDate) &&
        state.civil.cpf?.isNotBlank() == true && state.civil.email?.isNotBlank() == true &&
        plan != null && model != null && signers.isNotEmpty() && state.contract.pendingFields.isEmpty()
    PreparationStepLayout(
        title = "Revisão",
        description = "Confira as informações que serão congeladas no documento. Depois de gerar, elas não poderão ser alteradas.",
        error = state.actionError,
        primaryLabel = "Gerar contrato",
        primaryEnabled = ready && !state.submitting,
        submitting = state.submitting,
        onPrimary = onGenerate,
        onCancel = onCancel,
        modifier = modifier
    ) {
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
                SummaryLine("Gestante", state.civil.patientName)
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Bebê", state.patient.babyName.orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("DPP", state.patient.dueDate?.let(::formatBrazilianDate).orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Maternidade", state.patient.maternity?.takeIf(String::isNotBlank) ?: "Não informada")
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("CPF", state.civil.cpf.orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("E-mail", state.civil.email.orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Condição comercial", plan?.let { "${it.name} · ${formatContractPrice(it.price)}" }.orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Modelo", model?.name.orEmpty())
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Signatários", signers.joinToString { it.name })
            }
        }
        if (!ready) {
            PartejarAlert("Ainda há informações pendentes", PartejarAlertTone.ATTENTION) {
                Text("Revise as etapas anteriores antes de gerar o contrato.")
            }
        }
    }
}

@Composable
private fun PreparationStepLayout(
    title: String,
    description: String,
    error: String?,
    primaryLabel: String,
    primaryEnabled: Boolean,
    submitting: Boolean,
    onPrimary: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    val spacing = PartejarTheme.spacing
    Column(modifier.fillMaxWidth()) {
        Column(
            Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState())
                .padding(horizontal = spacing.space16, vertical = spacing.space16),
            verticalArrangement = Arrangement.spacedBy(spacing.space12)
        ) {
            Text(title, style = MaterialTheme.typography.titleLarge)
            Text(description, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            error?.let { PartejarAlert("Não foi possível continuar", PartejarAlertTone.ERROR) { Text(it) } }
            content()
            Spacer(Modifier.height(spacing.space16))
        }
        Surface(
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = spacing.space4,
            shadowElevation = spacing.space4
        ) {
            Row(
                Modifier.fillMaxWidth().imePadding().navigationBarsPadding()
                    .padding(horizontal = spacing.space16, vertical = spacing.space12),
                horizontalArrangement = Arrangement.spacedBy(spacing.space8)
            ) {
                PartejarButton(
                    "Cancelar",
                    onCancel,
                    Modifier.weight(1f),
                    variant = PartejarButtonVariant.SECONDARY,
                    enabled = !submitting,
                    fillWidth = false
                )
                PartejarButton(
                    primaryLabel,
                    onPrimary,
                    Modifier.weight(1f),
                    enabled = primaryEnabled,
                    isLoading = submitting,
                    fillWidth = false
                )
            }
        }
    }
}

@Composable
private fun ContractDetail(
    state: ContratoUiState.Content,
    selectedPlanId: Long?,
    selectedModelId: Long?,
    selectedSignerIds: Set<Long>,
    onEditPatient: () -> Unit,
    onSelectPlan: () -> Unit,
    onSelectModel: () -> Unit,
    onSelectSigners: () -> Unit,
    onEditCivilData: () -> Unit,
    onGenerate: () -> Unit,
    onPdf: () -> Unit,
    onLink: () -> Unit,
    onSignTeam: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    val spacing = PartejarTheme.spacing
    val editable = state.contract.status.isEditableContractStatus()
    ContractScrollable(modifier) {
        PatientCard(
            name = state.civil.patientName,
            babyName = state.contract.babyName,
            dueDate = state.contract.dueDate,
            onClick = if (editable || state.contract.dueDate == null) onEditPatient else null
        )
        ContractStatusBlock(state.contract.status)
        state.actionError?.let {
            PartejarAlert("Não foi possível concluir", PartejarAlertTone.ERROR) { Text(it) }
        }

        if (editable) {
            EditableContract(
                state = state,
                selectedPlanId = selectedPlanId,
                selectedModelId = selectedModelId,
                selectedSignerIds = selectedSignerIds,
                onSelectPlan = onSelectPlan,
                onSelectModel = onSelectModel,
                onSelectSigners = onSelectSigners,
                onEditCivilData = onEditCivilData,
                onGenerate = onGenerate
            )
        } else {
            GeneratedContract(
                state = state,
                onEditPatient = onEditPatient,
                onPdf = onPdf,
                onLink = onLink,
                onSignTeam = onSignTeam
            )
        }

        if (state.contract.status.canCancelContract()) {
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            PartejarButton(
                "Cancelar contrato",
                onCancel,
                variant = PartejarButtonVariant.DESTRUCTIVE_SECONDARY,
                size = PartejarButtonSize.COMPACT,
                motionEnabled = true
            )
        }
        Spacer(Modifier.height(spacing.space16))
    }
}

@Composable
private fun EditableContract(
    state: ContratoUiState.Content,
    selectedPlanId: Long?,
    selectedModelId: Long?,
    selectedSignerIds: Set<Long>,
    onSelectPlan: () -> Unit,
    onSelectModel: () -> Unit,
    onSelectSigners: () -> Unit,
    onEditCivilData: () -> Unit,
    onGenerate: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val pending = state.contract.pendingFields
    pendingAction(pending, selectedPlanId, selectedModelId, selectedSignerIds)?.let { next ->
        NextStepCard(next, when (next.destination) {
            ContractPage.PLAN -> onSelectPlan
            ContractPage.MODEL -> onSelectModel
            ContractPage.SIGNERS -> onSelectSigners
            ContractPage.CIVIL_DATA -> onEditCivilData
            ContractPage.DETAIL -> ({})
            ContractPage.PREPARATION -> ({})
        })
    }

    SectionTitle("Configuração do contrato")
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        val plan = state.options.plans.firstOrNull { it.versionId == selectedPlanId }
        ConfigurationRow(
            icon = PartejarIcons.Contracts.condition,
            title = "Condição comercial",
            value = plan?.let { "${it.name}\n${formatContractPrice(it.price)}" }
                ?: state.contract.planName?.let { "${it}\n${formatContractPrice(state.contract.planPrice)}" },
            onClick = onSelectPlan
        )
        val model = state.options.models.firstOrNull { it.versionId == selectedModelId }
        ConfigurationRow(
            icon = PartejarIcons.Contracts.template,
            title = "Modelo",
            value = model?.name ?: state.contract.modelName,
            onClick = onSelectModel
        )
        val signerNames = state.options.signers.filter { it.memberId in selectedSignerIds }.map { it.name }
        ConfigurationRow(
            icon = PartejarIcons.Contracts.signers,
            title = "Signatários",
            value = signerSummary(signerNames.ifEmpty { state.contract.signerNames }, selectedSignerIds.size),
            onClick = onSelectSigners
        )
    }

    val civilPending = pending.any { it == "CPF" || it == "E-mail" }
    if (civilPending) {
        SectionTitle("Dados da paciente")
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                CivilDataLine("CPF", state.civil.cpf)
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                CivilDataLine("E-mail", state.civil.email)
                PartejarButton(
                    "Completar dados da paciente",
                    onEditCivilData,
                    variant = PartejarButtonVariant.SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    leadingIcon = { Icon(PartejarIcons.Contracts.patient, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconSmall)) }
                )
            }
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        PartejarButton(
            "Gerar contrato",
            onGenerate,
            enabled = pending.isEmpty() && !state.submitting,
            isLoading = state.submitting,
            loadingDescription = "Gerando contrato",
            motionEnabled = true,
            leadingIcon = { Icon(PartejarIcons.Contracts.generate, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconMedium)) }
        )
        if (pending.isNotEmpty()) {
            Text(
                pendingExplanation(pending.size),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun GeneratedContract(
    state: ContratoUiState.Content,
    onEditPatient: () -> Unit,
    onPdf: () -> Unit,
    onLink: () -> Unit,
    onSignTeam: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    val contract = state.contract
    val hasDocument = contract.content != null

    if (hasDocument) {
        SectionTitle("Resumo do contrato")
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                SummaryLine("Condição comercial", listOfNotNull(contract.planName, contract.planPrice?.let(::formatContractPrice)).joinToString(" · ").ifBlank { "Não disponível" })
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Modelo", contract.modelName ?: "Não disponível")
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                SummaryLine("Signatários", contract.signerNames.joinToString().ifBlank { "Não disponível" })
            }
        }

        SectionTitle("Documento")
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ContractIcon(PartejarIcons.Contracts.document)
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                        Text(contract.modelName ?: "Contrato", style = MaterialTheme.typography.titleSmall)
                        formatBrazilianDateTime(contract.generatedAt)?.let {
                            Text("Gerado em $it", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
                PartejarButton(
                    "Visualizar contrato",
                    onPdf,
                    enabled = !state.submitting,
                    variant = PartejarButtonVariant.SECONDARY,
                    size = PartejarButtonSize.COMPACT,
                    leadingIcon = { Icon(PartejarIcons.Contracts.view, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconSmall)) }
                )
            }
        }
    }

    val signatures = state.signatures ?: pendingSignatures(state)
    if (hasDocument && contract.status != "CANCELADO") {
        SectionTitle("Assinaturas")
        SignatureTimeline(signatures)
    }

    val lastTeamSignatureBlocked = contract.dueDate == null &&
        signatures.patient.signed &&
        signatures.team.count { !it.signed } == 1 &&
        signatures.canCurrentMemberSign
    if (lastTeamSignatureBlocked) {
        NextStepCard(
            PendingAction(
                title = "Informar DPP da paciente",
                description = "A DPP é necessária para concluir o contrato e ativar a paciente.",
                action = "Completar dados da paciente",
                destination = ContractPage.DETAIL
            ),
            onEditPatient
        )
    }

    when (contract.status) {
        "PRONTO_PARA_ASSINATURA", "AGUARDANDO_ASSINATURA_PACIENTE" -> {
            if (state.link == null) {
                PartejarButton(
                    "Enviar link para a paciente",
                    onLink,
                    enabled = !state.submitting,
                    isLoading = state.submitting,
                    loadingDescription = "Preparando link",
                    motionEnabled = true,
                    leadingIcon = { Icon(PartejarIcons.Contracts.send, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconMedium)) }
                )
            } else {
                SignatureLinkActions(state.link, onGenerateNew = onLink, submitting = state.submitting)
            }
        }
        "AGUARDANDO_ASSINATURAS_EQUIPE" -> if (signatures.canCurrentMemberSign) {
            PartejarButton(
                "Assinar contrato",
                onSignTeam,
                enabled = !state.submitting && !lastTeamSignatureBlocked,
                isLoading = state.submitting,
                loadingDescription = "Registrando assinatura",
                motionEnabled = true,
                leadingIcon = { Icon(PartejarIcons.Contracts.signature, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconMedium)) }
            )
        }
        "ASSINADO" -> PartejarAlert("Contrato concluído", PartejarAlertTone.SUCCESS) {
            Text("Todas as assinaturas obrigatórias foram concluídas.")
        }
        "CANCELADO" -> if (!hasDocument) {
            PartejarAlert("Documento não gerado", PartejarAlertTone.INFO) {
                Text("Este contrato foi cancelado antes da geração do documento.")
            }
        }
    }
}

@Composable
private fun SignatureLinkActions(link: SignatureLink, onGenerateNew: () -> Unit, submitting: Boolean) {
    val clipboard = LocalClipboardManager.current
    var copied by remember(link.url) { mutableStateOf(false) }
    val lifecycleOwner = LocalLifecycleOwner.current
    val now by produceState(Clock.System.now(), link, lifecycleOwner) {
        lifecycleOwner.lifecycle.repeatOnLifecycle(Lifecycle.State.RESUMED) {
            while (isActive) {
                value = Clock.System.now()
                delay(1_000)
            }
        }
    }
    val expiry = signatureLinkExpiry(link, now)
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
        if (expiry.validity != SignatureLinkValidity.ACTIVE) {
            PartejarAlert(
                if (expiry.validity == SignatureLinkValidity.EXPIRED) "Link expirado" else "Validade indisponível",
                PartejarAlertTone.ATTENTION
            ) {
                Text(if (expiry.validity == SignatureLinkValidity.EXPIRED)
                    "Este link expirou. Gere um novo link para enviar à paciente."
                else "Não foi possível determinar a validade deste link. Gere um novo link. Se o problema persistir, tente novamente mais tarde.")
            }
        } else if (copied) {
            PartejarAlert("Link copiado", PartejarAlertTone.SUCCESS) {
                Text("O link de assinatura está pronto para ser enviado à paciente.")
            }
        }
        PartejarButton(
            when (expiry.validity) {
                SignatureLinkValidity.ACTIVE -> "Copiar link · expira em ${expiry.remaining}"
                SignatureLinkValidity.EXPIRED -> "Link expirado"
                SignatureLinkValidity.UNKNOWN -> "Validade indisponível"
            },
            {
                if (!submitting && signatureLinkExpiry(link, Clock.System.now()).validity == SignatureLinkValidity.ACTIVE) {
                    clipboard.setText(AnnotatedString(link.url))
                    copied = true
                }
            },
            enabled = !submitting && expiry.validity == SignatureLinkValidity.ACTIVE,
            motionEnabled = true,
            leadingIcon = { Icon(PartejarIcons.Contracts.send, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconMedium)) }
        )
        PartejarButton(
            "Gerar novo link",
            onGenerateNew,
            enabled = !submitting,
            variant = PartejarButtonVariant.TERTIARY,
            size = PartejarButtonSize.COMPACT
        )
    }
}

@Composable
private fun PatientCard(name: String, babyName: String?, dueDate: String?, onClick: (() -> Unit)?) {
    val spacing = PartejarTheme.spacing
    val content: @Composable () -> Unit = {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(spacing.space48),
                shape = PartejarShapes.pill,
                color = MaterialTheme.colorScheme.primaryContainer,
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(contractInitials(name), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                }
            }
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(name, style = MaterialTheme.typography.titleMedium)
                val metadata = listOfNotNull(
                    babyName?.takeIf(String::isNotBlank),
                    formatBrazilianDate(dueDate)?.let { "DPP $it" }
                )
                Text(
                    metadata.joinToString(" · ").ifBlank { "Dados da paciente" },
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            if (onClick != null) {
                Icon(PartejarIcons.Actions.next, contentDescription = "Abrir dados da paciente", Modifier.size(PartejarTheme.dimensions.iconDefault))
            }
        }
    }
    if (onClick != null) {
        PartejarCard(onClick = onClick, modifier = Modifier.fillMaxWidth(), motionEnabled = true, content = content)
    } else {
        PartejarCard(modifier = Modifier.fillMaxWidth(), content = content)
    }
}

@Composable
private fun ContractStatusBlock(status: String) {
    val presentation = contractStatusPresentation(status)
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
            Text("Situação do contrato", style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
            PartejarStatusBadge(presentation.label, tone = presentation.tone)
            Text(presentation.description, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
private fun ConfigurationRow(icon: ImageVector, title: String, value: String?, onClick: () -> Unit) {
    val spacing = PartejarTheme.spacing
    PartejarCard(onClick = onClick, modifier = Modifier.fillMaxWidth(), motionEnabled = true) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            ContractIcon(icon)
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(title, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Text(
                    value ?: "Não selecionado",
                    style = MaterialTheme.typography.bodyLarge,
                    color = if (value == null) PartejarTheme.colors.attention else MaterialTheme.colorScheme.onSurface
                )
            }
            Icon(PartejarIcons.Actions.next, contentDescription = "Alterar $title", Modifier.size(PartejarTheme.dimensions.iconDefault))
        }
    }
}

@Composable
private fun ContractIcon(icon: ImageVector) {
    Surface(
        modifier = Modifier.size(PartejarTheme.spacing.space40),
        shape = PartejarShapes.radius14,
        color = MaterialTheme.colorScheme.primaryContainer,
        contentColor = MaterialTheme.colorScheme.onPrimaryContainer
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(icon, contentDescription = null, modifier = Modifier.size(PartejarTheme.dimensions.iconMedium))
        }
    }
}

@Composable
private fun NextStepCard(action: PendingAction, onClick: () -> Unit) {
    val spacing = PartejarTheme.spacing
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = PartejarTheme.colors.attentionContainer,
        contentColor = PartejarTheme.colors.onAttentionContainer,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, PartejarTheme.colors.attention)
    ) {
        Column(Modifier.padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
            Text("Próximo passo", style = MaterialTheme.typography.labelLarge, color = PartejarTheme.colors.attention)
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(action.title, style = MaterialTheme.typography.titleMedium)
                Text(action.description, style = MaterialTheme.typography.bodyMedium)
            }
            PartejarButton(
                action.action,
                onClick,
                size = PartejarButtonSize.COMPACT,
                leadingIcon = { Icon(PartejarIcons.Actions.next, contentDescription = null, Modifier.size(PartejarTheme.dimensions.iconSmall)) }
            )
        }
    }
}

@Composable
private fun CivilDataLine(label: String, value: String?) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)) {
            Text(label, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value ?: "Não informado", style = MaterialTheme.typography.bodyLarge)
        }
        if (value == null) Icon(PartejarIcons.Feedback.attention, contentDescription = "Dado pendente", tint = PartejarTheme.colors.attention)
    }
}

@Composable
private fun SummaryLine(label: String, value: String) {
    Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)) {
        Text(label, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(value, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun SignatureTimeline(signatures: ContractSignatures) {
    val spacing = PartejarTheme.spacing
    PartejarCard(modifier = Modifier.fillMaxWidth()) {
        Column {
            SignatureRow(signatures.patient, "Aguardando assinatura da paciente")
            signatures.team.forEach { item ->
                HorizontalDivider(Modifier.padding(start = spacing.space40), color = MaterialTheme.colorScheme.outlineVariant)
                SignatureRow(item, if (signatures.patient.signed) "Aguardando assinatura da equipe" else "Assinará depois da paciente")
            }
        }
    }
}

@Composable
private fun SignatureRow(item: SignatureItem, pendingText: String) {
    val spacing = PartejarTheme.spacing
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = spacing.space12),
        horizontalArrangement = Arrangement.spacedBy(spacing.space12),
        verticalAlignment = Alignment.Top
    ) {
        val signed = item.signed
        Icon(
            if (signed) PartejarIcons.Feedback.success else PartejarIcons.Clinical.schedule,
            contentDescription = if (signed) "Assinado" else "Pendente",
            modifier = Modifier.size(PartejarTheme.dimensions.iconMedium),
            tint = if (signed) PartejarTheme.colors.success else MaterialTheme.colorScheme.onSurfaceVariant
        )
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            Text(item.name, style = MaterialTheme.typography.titleSmall)
            Text(
                if (signed) formatBrazilianDateTime(item.signedAt)?.let { "Assinado em $it" } ?: "Assinado" else pendingText,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun PlanSelection(options: List<ContractPlan>, selectedId: Long?, submitting: Boolean, error: String?, onConfirm: (Long) -> Unit, modifier: Modifier = Modifier) {
    var selection by remember(selectedId) { mutableStateOf(selectedId) }
    ContractScrollable(modifier) {
        Text("Escolha uma opção existente. O valor faz parte do plano e não pode ser editado aqui.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        options.forEach { plan ->
            SelectionOption(selected = selection == plan.versionId, onClick = { selection = plan.versionId }) {
                Text(plan.name, style = MaterialTheme.typography.titleSmall)
                Text(formatContractPrice(plan.price), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                if (plan.services.isNotEmpty()) Text(plan.services.joinToString(), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
        error?.let { PartejarAlert("Não foi possível salvar", PartejarAlertTone.ERROR) { Text(it) } }
        PartejarButton("Confirmar", { selection?.let(onConfirm) }, enabled = selection != null && !submitting, isLoading = submitting)
    }
}

@Composable
private fun ModelSelection(options: List<ContractModel>, selectedId: Long?, submitting: Boolean, error: String?, onConfirm: (Long) -> Unit, modifier: Modifier = Modifier) {
    var selection by remember(selectedId) { mutableStateOf(selectedId) }
    ContractScrollable(modifier) {
        Text("Selecione a versão de modelo que será usada ao gerar o documento.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        options.forEach { model ->
            SelectionOption(selected = selection == model.versionId, onClick = { selection = model.versionId }) {
                Text(model.name, style = MaterialTheme.typography.titleSmall)
            }
        }
        error?.let { PartejarAlert("Não foi possível salvar", PartejarAlertTone.ERROR) { Text(it) } }
        PartejarButton("Confirmar", { selection?.let(onConfirm) }, enabled = selection != null && !submitting, isLoading = submitting)
    }
}

@Composable
private fun SignerSelection(options: List<ContractSigner>, selectedIds: Set<Long>, submitting: Boolean, error: String?, onConfirm: (Set<Long>) -> Unit, modifier: Modifier = Modifier) {
    var selection by remember(selectedIds) { mutableStateOf(selectedIds) }
    ContractScrollable(modifier) {
        Text("Selecione ao menos um profissional. A paciente assina separadamente pelo link público.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        options.forEach { signer ->
            SignerOption(signer, signer.memberId in selection) { checked ->
                selection = if (checked) selection + signer.memberId else selection - signer.memberId
            }
        }
        error?.let { PartejarAlert("Não foi possível salvar", PartejarAlertTone.ERROR) { Text(it) } }
        PartejarButton("Salvar signatários", { onConfirm(selection) }, enabled = selection.isNotEmpty() && !submitting, isLoading = submitting)
    }
}

@Composable
private fun CivilDataEditor(civil: CivilData, submitting: Boolean, error: String?, onSave: (String, String) -> Unit, onCancel: () -> Unit, modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    val focus = LocalFocusManager.current
    val emailFocus = remember { FocusRequester() }
    val tapState = rememberTapOutsideFocusState()
    val cpfFocusBounds = rememberFocusedFieldBoundsModifier(tapState)
    val emailFocusBounds = rememberFocusedFieldBoundsModifier(tapState)
    val cpfVisibility = rememberBringIntoViewOnFocusModifier(actionClearance = spacing.space64)
    val emailVisibility = rememberBringIntoViewOnFocusModifier(actionClearance = spacing.space64)
    var cpf by remember(civil.cpf) { mutableStateOf(civil.cpf.orEmpty()) }
    var email by remember(civil.email) { mutableStateOf(civil.email.orEmpty()) }

    ContractScrollable(modifier.clearFocusOnTapOutside(tapState), imeAware = true) {
        Text("CPF e e-mail pertencem ao cadastro da paciente e são necessários para gerar o contrato.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        PartejarTextField(
            value = cpf,
            onValueChange = { cpf = it },
            label = "CPF",
            enabled = !submitting,
            modifier = cpfFocusBounds.then(cpfVisibility),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number, imeAction = ImeAction.Next),
            keyboardActions = KeyboardActions(onNext = { emailFocus.requestFocus() })
        )
        PartejarTextField(
            value = email,
            onValueChange = { email = it },
            label = "E-mail",
            enabled = !submitting,
            modifier = Modifier.focusRequester(emailFocus).then(emailFocusBounds).then(emailVisibility),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email, imeAction = ImeAction.Done),
            keyboardActions = KeyboardActions(onDone = { focus.clearFocus() })
        )
        error?.let { PartejarAlert("Não foi possível salvar", PartejarAlertTone.ERROR) { Text(it) } }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
            PartejarButton("Cancelar", onCancel, Modifier.weight(1f), variant = PartejarButtonVariant.SECONDARY, enabled = !submitting, fillWidth = false)
            PartejarButton("Salvar", { onSave(cpf, email) }, Modifier.weight(1f), enabled = cpf.isNotBlank() && email.isNotBlank() && !submitting, isLoading = submitting, fillWidth = false)
        }
    }
}

@Composable
private fun SelectionOption(selected: Boolean, onClick: () -> Unit, content: @Composable ColumnScope.() -> Unit) {
    Surface(
        modifier = Modifier.fillMaxWidth().selectable(selected, role = Role.RadioButton, onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = if (selected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceContainerLow,
        contentColor = if (selected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurface,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline.copy(alpha = 0.62f))
    ) {
        Row(Modifier.fillMaxWidth().padding(PartejarTheme.spacing.space16), horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12), verticalAlignment = Alignment.Top) {
            RadioButton(selected, onClick = null)
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4), content = content)
        }
    }
}

@Composable
private fun SignerOption(signer: ContractSigner, selected: Boolean, onChange: (Boolean) -> Unit) {
    val spacing = PartejarTheme.spacing
    Surface(
        modifier = Modifier.fillMaxWidth().toggleable(selected, role = Role.Checkbox, onValueChange = onChange),
        shape = MaterialTheme.shapes.large,
        color = if (selected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceContainerLow,
        border = BorderStroke(PartejarTheme.dimensions.strokeThin, if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outline.copy(alpha = 0.62f))
    ) {
        Row(Modifier.fillMaxWidth().padding(spacing.space16), horizontalArrangement = Arrangement.spacedBy(spacing.space12), verticalAlignment = Alignment.Top) {
            Checkbox(selected, onCheckedChange = null)
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(signer.name, style = MaterialTheme.typography.titleSmall)
                Text(humanizeCategory(signer.category), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                signer.registration?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant) }
            }
        }
    }
}

@Composable
private fun ContractScrollable(modifier: Modifier = Modifier, imeAware: Boolean = false, content: @Composable ColumnScope.() -> Unit) {
    val spacing = PartejarTheme.spacing
    Column(
        modifier = modifier
            .fillMaxSize()
            .then(if (imeAware) Modifier.imePadding() else Modifier)
            .verticalScroll(rememberScrollState())
            .padding(horizontal = spacing.space16, vertical = spacing.space12)
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom)),
        verticalArrangement = Arrangement.spacedBy(spacing.space16),
        content = content
    )
}

@Composable
private fun ContractLoading(modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    Column(modifier.fillMaxSize().padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        PartejarCard(Modifier.fillMaxWidth()) {
            Row(horizontalArrangement = Arrangement.spacedBy(spacing.space12)) {
                PartejarSkeleton(Modifier.size(spacing.space48), PartejarShapes.pill)
                Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.68f).height(spacing.space20))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.52f).height(spacing.space12))
                }
            }
        }
        repeat(3) {
            PartejarCard(Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.42f).height(spacing.space16))
                    PartejarSkeletonText(Modifier.fillMaxWidth().height(spacing.space12))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.76f).height(spacing.space12))
                }
            }
        }
    }
}

@Composable
private fun ContractLoadError(message: String, onRetry: () -> Unit, modifier: Modifier = Modifier) {
    Column(modifier.fillMaxSize().padding(PartejarTheme.spacing.space16), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
        PartejarAlert("Não foi possível abrir", PartejarAlertTone.ERROR) { Text(message) }
        PartejarButton("Tentar novamente", onRetry)
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(text, style = MaterialTheme.typography.titleMedium)
}

private data class PendingAction(val title: String, val description: String, val action: String, val destination: ContractPage)

private fun pendingAction(pending: List<String>, planId: Long?, modelId: Long?, signerIds: Set<Long>): PendingAction? = when {
    pending.any { it == "CPF" || it == "E-mail" } -> {
        val civil = pending.filter { it == "CPF" || it == "E-mail" }
        PendingAction(
            "Completar dados da paciente",
            "${civil.joinToString(" e ")} ${if (civil.size == 1) "é necessário" else "são necessários"} para gerar o contrato.",
            "Completar dados da paciente",
            ContractPage.CIVIL_DATA
        )
    }
    planId == null -> PendingAction("Escolher condição comercial", "Selecione o acompanhamento que será usado no contrato.", "Escolher condição comercial", ContractPage.PLAN)
    modelId == null -> PendingAction("Escolher modelo", "Selecione o modelo que dará origem ao documento.", "Escolher modelo", ContractPage.MODEL)
    signerIds.isEmpty() -> PendingAction("Selecionar signatários", "Escolha ao menos um profissional para assinar depois da paciente.", "Selecionar signatários", ContractPage.SIGNERS)
    else -> null
}

internal data class ContractStatusPresentation(val label: String, val description: String, val tone: PartejarStatusBadgeTone)

internal fun contractStatusPresentation(status: String): ContractStatusPresentation = when (status) {
    "RASCUNHO" -> ContractStatusPresentation("Rascunho", "Complete as informações necessárias para gerar o contrato.", PartejarStatusBadgeTone.NEUTRAL)
    "AGUARDANDO_DADOS" -> ContractStatusPresentation("Dados pendentes", "Faltam informações obrigatórias para gerar o contrato.", PartejarStatusBadgeTone.ATTENTION)
    "PRONTO_PARA_ASSINATURA" -> ContractStatusPresentation("Pronto para assinatura", "Documento gerado e pronto para ser enviado à paciente.", PartejarStatusBadgeTone.INFO)
    "AGUARDANDO_ASSINATURA_PACIENTE" -> ContractStatusPresentation("Aguardando assinatura da paciente", "O documento está disponível para a assinatura da paciente.", PartejarStatusBadgeTone.PRIMARY)
    "AGUARDANDO_ASSINATURAS_EQUIPE" -> ContractStatusPresentation("Aguardando assinaturas da equipe", "A paciente já assinou. Agora a equipe conclui as assinaturas.", PartejarStatusBadgeTone.PRIMARY)
    "ASSINADO" -> ContractStatusPresentation("Contrato assinado", "Todas as assinaturas obrigatórias foram concluídas.", PartejarStatusBadgeTone.SUCCESS)
    "CANCELADO" -> ContractStatusPresentation("Contrato cancelado", "Este contrato está disponível somente para consulta.", PartejarStatusBadgeTone.ERROR)
    else -> ContractStatusPresentation("Situação indisponível", "Não foi possível identificar a situação atual do contrato.", PartejarStatusBadgeTone.NEUTRAL)
}

internal fun String.isEditableContractStatus() = this == "RASCUNHO" || this == "AGUARDANDO_DADOS"
internal fun String.canCancelContract() = this != "ASSINADO" && this != "CANCELADO"
internal fun shouldOpenPatientAfterContract(status: String?): Boolean = status == "ASSINADO"

internal fun formatContractPrice(value: String?): String {
    val normalized = value?.trim()?.replace(',', '.') ?: return "Valor não disponível"
    val parts = normalized.split('.', limit = 2)
    val integer = parts.firstOrNull()?.filter(Char::isDigit)?.ifBlank { "0" } ?: "0"
    val grouped = integer.reversed().chunked(3).joinToString(".").reversed()
    val decimal = parts.getOrNull(1)?.filter(Char::isDigit).orEmpty().padEnd(2, '0').take(2)
    return "R$ $grouped,$decimal"
}

private fun contractInitials(name: String): String = name.trim().split(Regex("\\s+")).filter(String::isNotBlank).let { parts ->
    listOfNotNull(parts.firstOrNull()?.firstOrNull(), parts.drop(1).lastOrNull()?.firstOrNull()).joinToString("").uppercase()
}

private fun signerSummary(names: List<String>, selectedCount: Int): String = when {
    selectedCount == 0 -> "Nenhum profissional selecionado"
    names.size == 1 -> names.single()
    names.isNotEmpty() -> "$selectedCount profissionais selecionados\n${names.joinToString()}"
    selectedCount == 1 -> "1 profissional selecionado"
    else -> "$selectedCount profissionais selecionados"
}

private fun pendingExplanation(count: Int) = if (count == 1) "Falta 1 informação para gerar o contrato." else "Faltam $count informações para gerar o contrato."

private fun humanizeCategory(value: String): String = value.lowercase().split('_').joinToString(" ") { word ->
    word.replaceFirstChar { it.uppercase() }
}

private fun pendingSignatures(state: ContratoUiState.Content) = ContractSignatures(
    status = state.contract.status,
    canCurrentMemberSign = false,
    patient = SignatureItem(null, state.civil.patientName, false, null),
    team = state.contract.signerNames.map { SignatureItem(null, it, false, null) }
)
